import { auth, db } from '../../lib/firebase';
import { recordActivity } from '../../lib/services/activityService';
import { withTimeout } from '../utils/withTimeout';
import { firestoreWriteWithFallback } from '../utils/firestoreWriteWithFallback';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type FieldValue,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  getFirestore as getFirestoreLite,
  doc as docLite,
  setDoc as setDocLite,
  updateDoc as updateDocLite,
} from 'firebase/firestore/lite';
import {
  createRoom as cfCreateRoom,
  endRoomHTTP as cfEndRoom,
  getRoomState as cfGetRoomState,
  type CloudflareRoom,
} from './cloudflareSignaling';
import {
  addLiveRoomDiagnosticBreadcrumb,
  finishLiveRoomDiagnosticSpan,
  startLiveRoomDiagnosticSpan,
} from './liveRoomDiagnostics';
import { resolveLiveAudienceContract } from '../types/liveAudience';
import type { LiveAudienceContract } from '../types/liveAudience';
import {
  resolveLiveArchiveMode,
  resolveLiveArchivePolicy,
  type LiveArchiveMode,
  type LiveArchivePolicy,
} from '../types/liveArchive';
import {
  resolveLiveDeliveryContract,
  resolveLiveDeliveryMode,
  type LiveDeliveryContract,
  type LiveDeliveryMode,
} from '../types/liveDelivery';
import type { LiveEncoderProfile, LiveEncoderSetup, LiveHostRuntime } from '../types/liveEncoder';
import type { RecordingState } from '../types/recording';
import {
  assertValidPreflightConsent,
  type PreflightConsent,
} from '../lib/preflightConsent';

export interface LocalStream {
  id: string;
  userId: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  isLive: boolean;
  isLocal: true; // discriminator
  isVertical?: boolean;
  visibility?: 'public' | 'unlisted';
  startedAt: Date | Timestamp;
  endedAt?: Date | Timestamp;
  viewers: number;
  likes: number;
  comments: number;
  tags: string[];
  category: string;
  streamKey: string;
  rtmpUrl: string;
  secureRtmpUrl?: string | null;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: LiveArchivePolicy;
  deliveryMode?: LiveDeliveryMode;
  deliveryContract?: LiveDeliveryContract;
  hostRuntime?: LiveHostRuntime;
  encoderProfileId?: string;
  encoderProfile?: LiveEncoderProfile;
  encoderSetup?: LiveEncoderSetup;
}

export interface LiveRoom {
  id: string;
  hostId: string;
  hostName?: string; // for liveIndex display
  hostAvatar?: string | null; // for liveIndex display
  title: string;
  description: string;
  thumbnailUrl: string;
  isActive: boolean;
  isRoom: true; // discriminator
  roomType: 'meeting' | 'native' | 'shorts' | 'event' | 'battle' | 'audio'; // distinguishes room types
  isVertical?: boolean; // true for shorts (vertical layout)
  shortsMode?: 'broadcast' | 'interactive'; // Shorts Live viewer mode
  broadcastMeeting?: boolean;               // Meeting: also stream to watch page
  audienceContract?: LiveAudienceContract;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: LiveArchivePolicy;
  deliveryMode?: LiveDeliveryMode;
  deliveryContract?: LiveDeliveryContract;
  hostRuntime?: LiveHostRuntime;
  hlsPlaybackUrl?: string;
  dashPlaybackUrl?: string;
  startedAt: Date | Timestamp;
  endedAt?: Date | Timestamp;

  // Discovery shim metadata written on room creation
  hostLastSeen?: Timestamp | FieldValue;
  linkedEventId?: string;
  suppressDiscovery?: boolean;

  participants: number;
  maxParticipants: number;
  tags: string[];
  category: string;
  isPrivate: boolean;
  password?: string;
  allowedUsers?: string[];
  spotlightUserId?: string | null;
  // Battle mode
  opponentId?: string;
  battleDuration?: number;
  // Subscription gate
  requiredTier?: string | null;
  recording?: RecordingState;
}

const FIRESTORE_READY_TIMEOUT_MS = 5_000;
const DISCOVERY_WRITE_MAX_ATTEMPTS = 3;
const DISCOVERY_WRITE_BASE_DELAY_MS = 1_000;
const LIVE_GRID_VISIBILITY_DELAY_MS = 5_000;
const LIVE_START_CONFIRMATION_POLL_MS = 2_000;
const LIVE_START_CONFIRMATION_MAX_WAIT_MS = 60_000;
const ROOM_CREATE_TIMEOUT_MS = 25_000;
const ROOM_END_CONFIRM_TIMEOUT_MS = 6_000;
const dbLite = getFirestoreLite(auth.app);

// Re-export the Lite SDK helpers so other live services (e.g.
// replayPublishService) can use the same REST-based fallback path without
// duplicating the import + initialization.
export { dbLite, docLite, setDocLite, updateDocLite };

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getFirestoreErrorCode = (err: unknown): string | null => {
  const code = (err as { code?: unknown })?.code;
  return typeof code === 'string' ? code : null;
};

const RETRYABLE_CODES = new Set([
  'unauthenticated', 'failed-precondition', 'unavailable', 'aborted',
  'cancelled', 'deadline-exceeded', 'internal', 'resource-exhausted', 'unknown',
]);

const isRetryableDiscoveryError = (err: unknown): boolean => {
  const code = getFirestoreErrorCode(err);
  if (code && RETRYABLE_CODES.has(code)) return true;
  const msg = String((err as { message?: unknown })?.message ?? '').toLowerCase();
  return msg.includes('transport errored') || msg.includes('webchannel') ||
    msg.includes('connection') || msg.includes('timeout');
};

const waitForLiveRoomInactive = async (
  roomId: string,
  timeoutMs = ROOM_END_CONFIRM_TIMEOUT_MS,
): Promise<'inactive' | 'missing'> =>
  new Promise<'inactive' | 'missing'>((resolve, reject) => {
    let unsubscribe: Unsubscribe | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = (state: 'inactive' | 'missing') => {
      if (timeoutId) clearTimeout(timeoutId);
      try {
        unsubscribe?.();
      } catch {
        // noop
      }
      resolve(state);
    };

    timeoutId = setTimeout(() => {
      try {
        unsubscribe?.();
      } catch {
        // noop
      }
      reject(new Error('Timed out waiting for live room inactivity confirmation'));
    }, timeoutMs);

    unsubscribe = onSnapshot(
      doc(db, 'liveRooms', roomId),
      (snapshot) => {
        if (!snapshot.exists()) {
          finish('missing');
          return;
        }
        const data = snapshot.data() as Partial<LiveRoom>;
        if (data.isActive === false) {
          finish('inactive');
        }
      },
      () => {
        // Ignore listener errors here. The direct update path remains the primary
        // authority; this listener is only a confirmation channel.
      },
    );
  });

export const ensureFirestoreReady = async (userId: string, timeoutMs = FIRESTORE_READY_TIMEOUT_MS): Promise<void> => {
  const user = auth.currentUser;
  if (!user || user.uid !== userId) {
    throw new Error('Not authenticated with Firebase Auth');
  }

  const warmup = async () => {
    await user.getIdToken(true);
    await getDoc(doc(db, 'users', userId));
  };

  await withTimeout(warmup(), timeoutMs, 'Firestore warmup timed out');
};

type LiveRoomDiscoveryDoc = {
  id: string;
  hostId: string;
  hostName: string;
  hostAvatar: string | null;
  title: string;
  description: string;
  thumbnailUrl: string;
  isActive: boolean;
  isRoom: true;
  roomType: LiveRoom['roomType'];
  isVertical: boolean;
  shortsMode?: string;
  broadcastMeeting?: boolean;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: LiveArchivePolicy;
  deliveryMode?: LiveDeliveryMode;
  isPrivate: boolean;
  allowedUsers?: string[];
  maxParticipants: number;
  participants: number;
  tags: string[];
  category: string;
  startedAt: FieldValue | Date | Timestamp;
  hostLastSeen: FieldValue | Date | Timestamp;
  linkedEventId?: string;
  suppressDiscovery?: boolean;
  recording?: RecordingState;
  // Required on initial create (createLiveRoom asserts before write + the
  // firestore.rules `create` block enforces literal-true items). May be
  // omitted on `merge:true` recovery writes from syncLiveRoomDiscovery —
  // the existing field on the Firestore doc is preserved by the merge.
  preflightConsent?: PreflightConsent;
};

export type LiveRoomDiscoverySyncResult =
  | 'synced'
  | 'inactive'
  | 'waiting_for_participants'
  | 'error';

const normalizeTimestampLike = (value: unknown): Date | Timestamp => {
  if (value instanceof Date) return value;
  if (value && typeof (value as Timestamp).toDate === 'function') return value as Timestamp;
  return new Date();
};

const setDiscoveryDocViaLite = async (
  roomId: string,
  payload: LiveRoomDiscoveryDoc,
): Promise<void> => {
  await setDocLite(
    docLite(dbLite, 'liveRooms', roomId),
    {
      ...payload,
      startedAt: normalizeTimestampLike(payload.startedAt),
      hostLastSeen: new Date(),
    },
    { merge: true },
  );
};

const scheduleDiscoveryPublishAfterSuccessfulStart = (
  hostId: string,
  roomId: string,
  basePayload: LiveRoomDiscoveryDoc,
) => {
  void (async () => {
    await sleep(LIVE_GRID_VISIBILITY_DELAY_MS);

    const startTime = Date.now();
    while (Date.now() - startTime <= LIVE_START_CONFIRMATION_MAX_WAIT_MS) {
      let roomState: CloudflareRoom | null = null;
      try {
        roomState = await cfGetRoomState(roomId);
      } catch (err) {
        console.error('[liveService] Failed to verify room before grid publish:', err);
        return;
      }

      const participantCount = roomState?.participants ?? 0;
      if (!roomState?.isActive) {
        return;
      }

      if (participantCount >= 1) {
        const payload: LiveRoomDiscoveryDoc = {
          ...basePayload,
          startedAt: new Date(roomState.startedAt),
          participants: participantCount,
          hostLastSeen: serverTimestamp(),
        };

        writeLiveRoomDiscoveryDocInBackground(hostId, roomId, payload);
        return;
      }

      await sleep(LIVE_START_CONFIRMATION_POLL_MS);
    }
  })();
};

const writeLiveRoomDiscoveryDoc = async (
  hostId: string,
  roomId: string,
  payload: LiveRoomDiscoveryDoc,
): Promise<boolean> => {
  const streamRegistrationSpan = startLiveRoomDiagnosticSpan(
    'live.discovery_register',
    'live.stream.registration',
    {
      roomId,
      hostId,
      roomType: payload.roomType,
      attempt: 'initial',
    },
  );
  const roomRef = doc(db, 'liveRooms', roomId);

  for (let attempt = 0; attempt < DISCOVERY_WRITE_MAX_ATTEMPTS; attempt += 1) {
    // Fast path: try Lite SDK first (REST-based, avoids WebChannel entirely).
    try {
      await setDiscoveryDocViaLite(roomId, payload);
      addLiveRoomDiagnosticBreadcrumb('stream-registration', 'discovery_registered_lite', {
        roomId,
        attempt,
        roomType: payload.roomType,
      });
      finishLiveRoomDiagnosticSpan(streamRegistrationSpan, {
        roomId,
        hostId,
        status: 'success_lite',
        attempt,
      });
      return true;
    } catch {
      // Fallback to standard SDK path with WebChannel fallback below.
    }

    try {
      await ensureFirestoreReady(hostId);
      await firestoreWriteWithFallback(
        () => setDoc(roomRef, payload, { merge: true }),
        () => setDiscoveryDocViaLite(roomId, payload),
        { retries: 1, label: 'writeLiveRoomDiscoveryDoc' },
      );
      addLiveRoomDiagnosticBreadcrumb('stream-registration', 'discovery_registered', {
        roomId,
        attempt,
        roomType: payload.roomType,
      });
      finishLiveRoomDiagnosticSpan(streamRegistrationSpan, {
        roomId,
        hostId,
        status: 'success',
        attempt,
      });
      return true;
    } catch (err) {
      const retryable = isRetryableDiscoveryError(err);
      if (!retryable || attempt >= DISCOVERY_WRITE_MAX_ATTEMPTS - 1) {
        console.error('[liveService] Failed to write liveRooms discovery doc:', err);
        addLiveRoomDiagnosticBreadcrumb('stream-registration', 'discovery_registration_failed', {
          roomId,
          attempt,
          roomType: payload.roomType,
          retryable,
          code: getFirestoreErrorCode(err) ?? 'unknown',
        });
        finishLiveRoomDiagnosticSpan(streamRegistrationSpan, {
          roomId,
          hostId,
          status: 'failed',
          attempt,
          retryable,
          code: getFirestoreErrorCode(err) ?? 'unknown',
        });
        return false;
      }
      addLiveRoomDiagnosticBreadcrumb('stream-registration', 'discovery_registration_retry', {
        roomId,
        attempt,
        roomType: payload.roomType,
        code: getFirestoreErrorCode(err) ?? 'unknown',
      });
      // Exponential backoff: 1s, 2s, 4s
      await sleep(DISCOVERY_WRITE_BASE_DELAY_MS * Math.pow(2, attempt));
    }
  }

  finishLiveRoomDiagnosticSpan(streamRegistrationSpan, {
    roomId,
    hostId,
    status: 'exhausted',
  });
  return false;
};

const writeLiveRoomDiscoveryDocInBackground = (
  hostId: string,
  roomId: string,
  payload: LiveRoomDiscoveryDoc,
) => {
  void writeLiveRoomDiscoveryDoc(hostId, roomId, payload).catch(() => {
    // writeLiveRoomDiscoveryDoc already logs errors internally
  });
};

// Local Stream Functions
export const startLocalStream = async (
  userId: string,
  streamData: Omit<LocalStream, 'id' | 'userId' | 'startedAt' | 'viewers' | 'likes' | 'comments'>
): Promise<LocalStream> => {
  const archiveMode = resolveLiveArchiveMode({
    source: 'native',
    deliveryMode: streamData.deliveryMode,
    archiveMode: streamData.archiveMode,
    archivePolicy: streamData.archivePolicy,
  });
  const streamRef = doc(collection(db, 'localStreams'));
  const stream: LocalStream = {
    id: streamRef.id,
    userId,
    ...streamData,
    isLocal: true,
    archiveMode,
    archivePolicy: resolveLiveArchivePolicy({
      source: 'native',
      deliveryMode: streamData.deliveryMode,
      archiveMode,
      archivePolicy: streamData.archivePolicy,
    }),
    startedAt: new Date(),
    viewers: 0,
    likes: 0,
    comments: 0,
  };

  await setDoc(streamRef, stream);

  // Record activity
  recordActivity(userId, 'live_host', `Started live: ${streamData.title || 'Untitled'}`, undefined, {
    streamId: streamRef.id,
  }).catch(() => {});

  return stream;
};

export const endLocalStream = async (streamId: string): Promise<void> => {
  const streamRef = doc(db, 'localStreams', streamId);
  await updateDoc(streamRef, {
    isLive: false,
    endedAt: new Date(),
  });
  // Delete liveIndex entry (safety net — Cloud Function should also delete it)
  const indexRef = doc(db, 'liveIndex', streamId);
  deleteDoc(indexRef).catch(() => {});

  // Record activity
  const uid = auth.currentUser?.uid;
  if (uid) {
    recordActivity(uid, 'live_end', 'Ended live stream', undefined, { streamId }).catch(() => {});
  }
};

// ── Live Room Functions (Cloudflare Worker) ──────────────────────────────
//
// Room creation, signaling, presence, and chat are handled by
// Cloudflare Workers + Durable Objects. Firestore is only used for
// discovery shim writes (liveRooms -> onLiveRoomChange -> liveIndex).

/**
 * Creates a live room via the Cloudflare Worker.
 * Returns a LiveRoom-compatible object immediately.
 */
export const createLiveRoom = async (
  hostId: string,
  roomData: Omit<LiveRoom, 'id' | 'hostId' | 'startedAt' | 'participants' | 'isActive' | 'hostLastSeen'>,
  extra?: {
    enableRecording?: boolean;
    linkedEventId?: string;
    suppressDiscovery?: boolean;
    preflightConsent?: PreflightConsent;
  }
): Promise<LiveRoom> => {
  // T12 (LAUNCH_PLAN.md) — preflight consent is REQUIRED on every room
  // creation. Asserted BEFORE cfCreateRoom so a forged/missing consent
  // cannot create a phantom Cloudflare Worker room that lacks a Firestore
  // discovery doc.
  const preflightConsent = extra?.preflightConsent;
  assertValidPreflightConsent(preflightConsent, hostId);
  const createRoomSpan = startLiveRoomDiagnosticSpan('live.room_create', 'live.bootstrap', {
    hostId,
    roomType: roomData.roomType,
    title: roomData.title,
  });
  addLiveRoomDiagnosticBreadcrumb('bootstrap', 'create_room_requested', {
    hostId,
    roomType: roomData.roomType,
    title: roomData.title,
  });
  try {
    const archiveMode = resolveLiveArchiveMode({
      source: roomData.roomType,
      deliveryMode: roomData.deliveryMode,
      archiveMode: roomData.archiveMode,
      archivePolicy: roomData.archivePolicy,
    });
    const archivePolicy = resolveLiveArchivePolicy({
      source: roomData.roomType,
      deliveryMode: roomData.deliveryMode,
      archiveMode,
      archivePolicy: roomData.archivePolicy,
    });

    const cfRoom = await withTimeout(
      cfCreateRoom({
        title: roomData.title,
        description: roomData.description || '',
        roomType: roomData.roomType,
        isVertical: roomData.isVertical,
        shortsMode: roomData.shortsMode,
        broadcastMeeting: roomData.broadcastMeeting,
        deliveryMode: roomData.deliveryMode,
        archiveMode,
        archivePolicy,
        isPrivate: roomData.isPrivate,
        maxParticipants: roomData.maxParticipants,
        category: roomData.category,
        tags: roomData.tags,
        thumbnailUrl: roomData.thumbnailUrl,
        hostName: roomData.hostName,
        hostAvatar: roomData.hostAvatar,
        password: roomData.password,
        allowedUsers: roomData.allowedUsers,
        opponentId: roomData.opponentId,
        battleDuration: roomData.battleDuration,
        enableRecording: extra?.enableRecording,
      }),
      ROOM_CREATE_TIMEOUT_MS,
      'Cloudflare room creation timed out'
    );
    addLiveRoomDiagnosticBreadcrumb('bootstrap', 'create_room_resolved', {
      roomId: cfRoom.id,
      roomType: roomData.roomType,
    });

    const liveRoom = cloudflareToLiveRoom(
      cfRoom,
      hostId,
      roomData.deliveryMode,
      archiveMode,
      archivePolicy,
      {
        broadcastMeeting: roomData.broadcastMeeting,
        shortsMode: roomData.shortsMode,
        linkedEventId: extra?.linkedEventId,
        suppressDiscovery: extra?.suppressDiscovery,
      },
    );

    // Write a minimal Firestore doc so the Cloud Function trigger
    // (onLiveRoomChange) fires and populates the liveIndex for discovery.
    const discoveryDoc: LiveRoomDiscoveryDoc = {
      id: cfRoom.id,
      hostId,
      hostName: roomData.hostName || '',
      hostAvatar: roomData.hostAvatar || null,
      title: roomData.title,
      description: roomData.description || '',
      thumbnailUrl: roomData.thumbnailUrl || '',
      isActive: true,
      isRoom: true,
      roomType: roomData.roomType,
      isVertical: roomData.isVertical || false,
      ...(liveRoom.shortsMode != null ? { shortsMode: liveRoom.shortsMode } : {}),
      ...(liveRoom.broadcastMeeting != null ? { broadcastMeeting: liveRoom.broadcastMeeting } : {}),
      ...(liveRoom.archiveMode != null ? { archiveMode: liveRoom.archiveMode } : {}),
      ...(liveRoom.archivePolicy ? { archivePolicy: liveRoom.archivePolicy } : {}),
      ...(liveRoom.deliveryMode != null ? { deliveryMode: liveRoom.deliveryMode } : {}),
      isPrivate: roomData.isPrivate || false,
      allowedUsers: roomData.allowedUsers || [],
      maxParticipants: roomData.maxParticipants || 9999,
      participants: 0,
      tags: roomData.tags || [],
      category: roomData.category || '',
      startedAt: serverTimestamp(),
      hostLastSeen: serverTimestamp(),
      ...(extra?.linkedEventId ? { linkedEventId: extra.linkedEventId } : {}),
      ...(extra?.suppressDiscovery ? { suppressDiscovery: true } : {}),
      ...(liveRoom.hlsPlaybackUrl ? { hlsPlaybackUrl: liveRoom.hlsPlaybackUrl } : {}),
      ...(liveRoom.dashPlaybackUrl ? { dashPlaybackUrl: liveRoom.dashPlaybackUrl } : {}),
      ...(liveRoom.recording ? { recording: liveRoom.recording } : {}),
      preflightConsent,
    };

    // Keep first-click launch path non-blocking for room modes created from
    // LiveStart (native/shorts/meeting). Discovery shim writes still run with
    // retries in the background and can populate liveIndex shortly after launch.
    const shouldDecoupleDiscoveryWrite =
      roomData.roomType === 'meeting' ||
      roomData.roomType === 'native' ||
      roomData.roomType === 'shorts';

    if (shouldDecoupleDiscoveryWrite) {
      addLiveRoomDiagnosticBreadcrumb('stream-registration', 'discovery_publish_deferred', {
        roomId: cfRoom.id,
        roomType: roomData.roomType,
      });
      scheduleDiscoveryPublishAfterSuccessfulStart(hostId, cfRoom.id, discoveryDoc);
    } else {
      // Other room types keep the previous blocking behavior.
      await writeLiveRoomDiscoveryDoc(hostId, cfRoom.id, discoveryDoc);
    }

    // Record activity
    recordActivity(hostId, 'live_host', `Started live room: ${roomData.title || 'Untitled'}`, undefined, {
      roomId: cfRoom.id,
      roomType: roomData.roomType,
    }).catch(() => {});

    finishLiveRoomDiagnosticSpan(createRoomSpan, {
      roomId: cfRoom.id,
      roomType: roomData.roomType,
      status: 'success',
    });
    return liveRoom;
  } catch (err) {
    finishLiveRoomDiagnosticSpan(createRoomSpan, {
      hostId,
      roomType: roomData.roomType,
      status: 'failed',
      reason: err instanceof Error ? err.message : 'unknown',
    });
    throw err;
  }
};

/** Convert Cloudflare room state to the LiveRoom interface used by UI components. */
export function cloudflareToLiveRoom(
  cf: CloudflareRoom,
  hostId: string,
  deliveryModeOverride?: LiveDeliveryMode,
  archiveModeOverride?: LiveArchiveMode,
  archivePolicyOverride?: LiveArchivePolicy,
  roomMetadataOverrides?: {
    broadcastMeeting?: boolean;
    shortsMode?: 'broadcast' | 'interactive';
    allowedUsers?: string[];
    linkedEventId?: string;
    suppressDiscovery?: boolean;
  },
): LiveRoom {
  // shortsMode resolution priority:
  // 1. Explicit override (e.g. from Firestore shim)
  // 2. Explicit field from Worker room state (present on all rooms created after shortsMode was added)
  // 3. Legacy fallback: infer 'broadcast' from isVertical===false for rooms created before shortsMode existed
  //    Safe to remove once all pre-shortsMode rooms have ended (no new rooms use this path).
  const resolvedShortsMode =
    roomMetadataOverrides?.shortsMode ??
    (cf.shortsMode as 'broadcast' | 'interactive' | undefined) ??
    (cf.roomType === 'shorts' && cf.isVertical === false ? 'broadcast' : undefined);
  const resolvedBroadcastMeeting =
    roomMetadataOverrides?.broadcastMeeting ?? cf.broadcastMeeting;
  const deliveryMode = resolveLiveDeliveryMode({
    source: cf.roomType as LiveRoom['roomType'],
    shortsMode: resolvedShortsMode,
    broadcastMeeting: resolvedBroadcastMeeting,
    deliveryMode: deliveryModeOverride ?? cf.deliveryMode,
  });
  const audienceContract = resolveLiveAudienceContract({
    source: cf.roomType as LiveRoom['roomType'],
    shortsMode: resolvedShortsMode,
    broadcastMeeting: resolvedBroadcastMeeting,
  });
  const deliveryContract = resolveLiveDeliveryContract({
    source: cf.roomType as LiveRoom['roomType'],
    shortsMode: resolvedShortsMode,
    broadcastMeeting: resolvedBroadcastMeeting,
    deliveryMode,
  });
  const archiveMode = resolveLiveArchiveMode({
    source: cf.roomType as LiveRoom['roomType'],
    deliveryMode,
    archiveMode: archiveModeOverride ?? cf.archiveMode,
    archivePolicy: archivePolicyOverride ?? cf.archivePolicy,
  });
  const archivePolicy = resolveLiveArchivePolicy({
    source: cf.roomType as LiveRoom['roomType'],
    deliveryMode,
    archiveMode,
    archivePolicy: archivePolicyOverride ?? cf.archivePolicy,
  });
  return {
    id: cf.id,
    hostId: hostId || cf.hostId,
    hostName: cf.hostName,
    hostAvatar: cf.hostAvatar,
    title: cf.title,
    description: cf.description,
    thumbnailUrl: cf.thumbnailUrl,
    isActive: cf.isActive,
    isRoom: true,
    roomType: cf.roomType as LiveRoom['roomType'],
    isVertical: cf.isVertical,
    shortsMode: resolvedShortsMode,
    broadcastMeeting: resolvedBroadcastMeeting,
    audienceContract,
    archiveMode,
    archivePolicy,
    deliveryMode,
    deliveryContract,
    startedAt: new Date(cf.startedAt),
    participants: cf.participants,
    maxParticipants: cf.maxParticipants,
    tags: cf.tags,
    category: cf.category,
    isPrivate: cf.isPrivate,
    allowedUsers: roomMetadataOverrides?.allowedUsers,
    spotlightUserId: cf.spotlightUserId,
    hlsPlaybackUrl: cf.hlsPlaybackUrl,
    dashPlaybackUrl: cf.dashPlaybackUrl,
    recording: cf.recording,
    linkedEventId: roomMetadataOverrides?.linkedEventId,
    suppressDiscovery: roomMetadataOverrides?.suppressDiscovery,
  };
}

export const endLiveRoom = async (roomId: string): Promise<void> => {
  const endRoomSpan = startLiveRoomDiagnosticSpan('live.room_end', 'live.cleanup', {
    roomId,
  });
  addLiveRoomDiagnosticBreadcrumb('cleanup', 'room_end_requested', { roomId });

  try {
    await cfEndRoom(roomId);
    addLiveRoomDiagnosticBreadcrumb('cleanup', 'room_end_worker_completed', { roomId });

    // Mark Firestore doc inactive so Cloud Function removes liveIndex entry.
    // The onLiveRoomChange trigger fires on this update and calls removeLiveIndex().
    const roomRef = doc(db, 'liveRooms', roomId);
    try {
      await firestoreWriteWithFallback(
        () => updateDoc(roomRef, { isActive: false, endedAt: serverTimestamp() }),
        async () => {
          try {
            await updateDocLite(docLite(dbLite, 'liveRooms', roomId), {
              isActive: false,
              endedAt: new Date(),
            });
          } catch {
            await setDocLite(
              docLite(dbLite, 'liveRooms', roomId),
              { id: roomId, isActive: false, endedAt: new Date() },
              { merge: true },
            );
          }
        },
        { retries: 1, label: 'endLiveRoom' },
      );
    } catch {
      // Last resort: setDoc with merge via standard SDK.
      try {
        await setDoc(
          roomRef,
          { id: roomId, isActive: false, endedAt: serverTimestamp() },
          { merge: true },
        );
      } catch (setErr) {
        console.error('[liveService] Failed to deactivate liveRooms doc:', setErr);
      }
    }

    // Safety net: directly delete liveIndex entry in case Cloud Function trigger is delayed
    const indexRef = doc(db, 'liveIndex', roomId);
    deleteDoc(indexRef).catch(() => {});

    const inactiveState = await waitForLiveRoomInactive(roomId);
    addLiveRoomDiagnosticBreadcrumb('cleanup', 'room_end_firestore_confirmed', {
      roomId,
      inactiveState,
    });
    finishLiveRoomDiagnosticSpan(endRoomSpan, {
      roomId,
      status: 'confirmed',
      inactiveState,
    });

    // Record activity
    const uid = auth.currentUser?.uid;
    if (uid) {
      recordActivity(uid, 'live_end', 'Ended live room', undefined, { roomId }).catch(() => {});
    }
  } catch (err) {
    addLiveRoomDiagnosticBreadcrumb('cleanup', 'room_end_failed', {
      roomId,
      reason: err instanceof Error ? err.message : String(err),
    });
    finishLiveRoomDiagnosticSpan(endRoomSpan, {
      roomId,
      status: 'failed',
      reason: err instanceof Error ? err.message : 'unknown',
    });
    throw err;
  }
};

/**
 * Host-side discovery reconciliation for active Cloudflare rooms.
 * Used by room page to recover visibility if launch-time shim writes fail.
 * Single non-retried write attempt via Lite SDK — the primary write path
 * (writeLiveRoomDiscoveryDoc) already retries with backoff; this is just a
 * periodic recovery check so a single attempt is sufficient.
 */
export const syncLiveRoomDiscovery = async (room: LiveRoom): Promise<LiveRoomDiscoverySyncResult> => {
  if (!room.hostId) return 'error';
  let roomState: CloudflareRoom | null = null;
  try {
    roomState = await cfGetRoomState(room.id);
  } catch {
    return 'error';
  }
  if (!roomState?.isActive) return 'inactive';
  if ((roomState.participants || 0) < 1) return 'waiting_for_participants';

  const payload: LiveRoomDiscoveryDoc = {
    id: room.id,
    hostId: room.hostId,
    hostName: room.hostName || '',
    hostAvatar: room.hostAvatar || null,
    title: room.title || 'Untitled Room',
    description: room.description || '',
    thumbnailUrl: room.thumbnailUrl || '',
    isActive: room.isActive !== false,
    isRoom: true,
    roomType: room.roomType,
    isVertical: room.isVertical || false,
    ...(room.shortsMode ? { shortsMode: room.shortsMode } : {}),
    ...(room.broadcastMeeting != null ? { broadcastMeeting: room.broadcastMeeting } : {}),
    isPrivate: room.isPrivate || false,
    maxParticipants: room.maxParticipants || 9999,
    participants: roomState.participants || room.participants || 0,
    tags: room.tags || [],
    category: room.category || '',
    startedAt: new Date(roomState.startedAt),
    hostLastSeen: new Date(),
    ...(Array.isArray(room.allowedUsers) ? { allowedUsers: room.allowedUsers } : {}),
    ...(room.archiveMode ? { archiveMode: room.archiveMode } : {}),
    ...(room.archivePolicy ? { archivePolicy: room.archivePolicy } : {}),
    ...(room.deliveryMode ? { deliveryMode: room.deliveryMode } : {}),
  };

  try {
    await setDiscoveryDocViaLite(room.id, payload);
    return 'synced';
  } catch (err) {
    console.error('[liveService] syncLiveRoomDiscovery failed:', err);
    return 'error';
  }
};

export const subscribeToLiveRoomMetadata = (
  roomId: string,
  callback: (room: Partial<LiveRoom> | null) => void,
): Unsubscribe =>
  onSnapshot(
    doc(db, 'liveRooms', roomId),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback(snapshot.data() as Partial<LiveRoom>);
    },
    (error) => {
      console.error('[liveService] Failed to subscribe to liveRooms metadata:', error);
      callback(null);
    },
  );

export async function getNextShortsRoom(currentRoomId: string): Promise<string | null> {
  const q = query(
    collection(db, 'liveIndex'),
    where('roomType', '==', 'shorts'),
    where('isActive', '==', true),
    where('shortsMode', '==', 'broadcast'),
    limit(10),
  );

  const snap = await getDocs(q);
  const rooms = snap.docs
    .map((d) => d.id)
    .filter((id) => id !== currentRoomId);

  return rooms[0] ?? null;
}
